import { isApiError } from '@shared/api';
import { takeHandoffSessionId } from '@shared/auth/session-coordination';
import { findAccount } from '@shared/lib/accounts';
import { authService } from '@shared/services/auth.service';
import { useAuthStore } from '@shared/store/auth.store';
import { storage } from '@shared/utils/storage';

/**
 * Restore the Identity session this page continues: the one handed over by
 * the application switcher when the page was opened through it (it takes
 * precedence), otherwise the one this application last used. The session is
 * rotated through `/auth/refresh` — the HttpOnly cookie is the only credential
 * that survives a page load — and its user and authorization are applied.
 *
 * `isCancelled` lets a caller that no longer wants the result (an effect that
 * was cleaned up) leave the store untouched. React runs the bootstrap effect
 * twice in development; both runs name the same session
 * (`takeHandoffSessionId`), so they share one refresh.
 *
 * When it fails:
 *  - refused (401/403): the session has ended. What it left in this browser
 *    goes, and when a session was expected the sign-in page says it ended.
 *  - not answered (identity unreachable, 5xx, 429): the session may still be
 *    valid, so nothing is purged and a retry is offered instead of a sign-in
 *    form, which would only start a second session.
 */
export async function restoreSession(isCancelled: () => boolean = () => false): Promise<void> {
  const store = useAuthStore.getState();
  const handedOver = takeHandoffSessionId();
  const sessionId =
    handedOver ??
    store.activeSessionId ??
    findAccount(store.accounts, store.activeAccountId)?.sessionId ??
    null;
  try {
    if (store.accessToken && !handedOver) {
      const user = await authService.getCurrentUser();
      if (!isCancelled()) {
        store.setUser(user);
        store.rememberAccount(user);
      }
      return;
    }
    const session = await authService.refresh(sessionId);
    if (isCancelled()) return;
    const user = await authService.resolveAuthorizedUser(session);
    if (isCancelled()) return;
    store.setSession(user, session, session);
    store.rememberAccount(user, session);
  } catch (error) {
    if (isCancelled()) return;
    const refused = isApiError(error) && (error.status === 401 || error.status === 403);
    store.clear();
    if (refused) storage.purgeSessionScoped();
    store.setSessionRestore(refused ? (sessionId ? 'ended' : null) : 'unavailable');
  } finally {
    if (!isCancelled()) store.setInitializing(false);
  }
}

/** "Try again", after a restore that could not be answered. */
export function retrySessionRestore(): Promise<void> {
  const store = useAuthStore.getState();
  store.setSessionRestore(null);
  store.setInitializing(true);
  return restoreSession();
}
